import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { TenantOption } from "@autorepair/contracts";
import { authApi, ApiError } from "./api";
import { useAuth } from "./useAuth";

/**
 * All the login/2FA/tenant-pick/forced-password-change state and handlers,
 * used by `LoginPageModern.tsx` (the only login screen this app has —
 * see docs/FRONTEND_BACKLOG.md Round 59 and docs/TRAPS.md for the
 * now-removed Classic theme this was originally extracted alongside).
 */
export function useLoginFlow() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [tenants, setTenants] = useState<TenantOption[] | null>(null);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  function handleLoginResult(result: Awaited<ReturnType<typeof authApi.login>>): void {
    if (result.kind === "session") {
      // Clear anything left over from whoever/whatever tenant was signed
      // in on this tab last — see auth.tsx's logout() for why a stale
      // cross-tenant cache is a real (if frontend-only) leak.
      queryClient.clear();
      setSession(result.session);
      navigate("/");
    } else if (result.kind === "needs_totp") {
      setNeedsTotp(true);
    } else if (result.kind === "needs_password_change") {
      setNeedsPasswordChange(true);
    } else {
      setTenants(result.tenants);
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await authApi.login(email, password);
      handleLoginResult(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerifyTotp(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await authApi.verifyTotp(totpCode, rememberDevice);
      handleLoginResult(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Invalid or expired code.");
    } finally {
      setSubmitting(false);
    }
  }

  async function pickTenant(tenantId: number): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const result = await authApi.selectTenant(tenantId);
      handleLoginResult(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleChangePasswordFirstLogin(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmNewPassword) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await authApi.changePasswordFirstLogin(newPassword);
      handleLoginResult(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    error,
    submitting,
    tenants,
    needsTotp,
    totpCode,
    setTotpCode,
    rememberDevice,
    setRememberDevice,
    needsPasswordChange,
    newPassword,
    setNewPassword,
    confirmNewPassword,
    setConfirmNewPassword,
    handleSubmit,
    handleVerifyTotp,
    pickTenant,
    handleChangePasswordFirstLogin,
  };
}
